import { useState } from 'react';
import type { IRepoQualityRow } from '@skystone/shared';
import { useMediaQuery } from '@/hooks/use-media-query';
import { RankedPanelShell } from './RankedPanelShell';
import { RepoQualityRow } from './RepoQualityRow';

interface LowestCoveragePanelProps {
  rows: IRepoQualityRow[];
}

function formatCoverage(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(1)}%`;
}

/**
 * Lists every in-scope repo sorted by coveragePct asc, nulls last.
 * Renders the bottom of the distribution; coverage cell shows "X.X%" or "—".
 */
export function LowestCoveragePanel({ rows }: Readonly<LowestCoveragePanelProps>) {
  const [expanded, setExpanded] = useState(false);
  const isSmUp = useMediaQuery('(min-width: 640px)');
  const defaultLimit = isSmUp ? 10 : 5;
  const visibleRows = expanded ? rows : rows.slice(0, defaultLimit);

  return (
    <RankedPanelShell
      testId="lowest-coverage-panel"
      title="Latest coverage — lowest first"
      countLabel={`(${rows.length})`}
      metricKey="cq_coverage"
      totalRows={rows.length}
      defaultLimit={defaultLimit}
      expanded={expanded}
      onToggleExpanded={() => setExpanded((v) => !v)}
      emptyState={rows.length === 0 ? 'No in-scope repos with coverage data yet.' : null}
    >
      {visibleRows.map((row) => (
        <RepoQualityRow
          key={row.repositoryId}
          repositoryId={row.repositoryId}
          repositoryName={row.repositoryName}
          projectKey={row.projectKey}
          organization={row.organization}
          lastAnalysedAt={row.lastAnalysedAt}
          valueSlot={
            <span className="font-mono text-xs text-foreground">{formatCoverage(row.coveragePct)}</span>
          }
        />
      ))}
    </RankedPanelShell>
  );
}
